import { useEffect, useRef, useState } from "react";
import { ChevronDown, Menu, X } from "lucide-react";
import { MENU_SERVICES, NAV_LINKS } from "../data/siteLinks";

interface NavigationProps {
  scrolled: boolean;
  page: string;
}

const CONTACT = { label: "Get in Touch", href: "/contact.html" };
// The phone menu's links share one centered outlined style; only Get in Touch is filled (Alan, 2026-11-03).
const menuLink =
  "block px-4 py-3 border border-gray-700/60 text-gray-200 hover:text-white hover:bg-gray-800/50 rounded-xl text-center transition-colors";

// One header on every page: the logo goes home, and phone and computer offer the same links (Alan, 2026-11-04).
const Navigation = ({ scrolled, page }: NavigationProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const servicesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setServicesOpen(false);
        setMenuOpen(false);
      }
    };
    const onClick = (e: MouseEvent) => {
      if (!servicesRef.current?.contains(e.target as Node)) setServicesOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("click", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("click", onClick);
    };
  }, []);

  // The home page shows the big wordmark at the top, so the small one joins the logo once you scroll past it.
  const showWordmark = page !== "home" || scrolled;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-gray-950 border-b border-white/10">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-4">
        <a href="/" className="flex items-center gap-3" aria-label="RareWorks home">
          <img src="/rareWorks.webp" alt="" className="h-12 w-auto rounded-xl" />
          <span
            className={`text-xl font-semibold transition-opacity duration-200 ${showWordmark ? "opacity-100" : "opacity-0"}`}
          >
            <span className="text-white">Rare</span>
            <span className="text-gradient">Works</span>
          </span>
        </a>

        <nav aria-label="Main" className="hidden lg:flex items-center gap-1">
          <div ref={servicesRef} className="relative">
            <button
              type="button"
              aria-expanded={servicesOpen}
              onClick={() => setServicesOpen((open) => !open)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors"
            >
              Services
              <ChevronDown className={`w-4 h-4 transition-transform duration-150 ${servicesOpen ? "rotate-180" : ""}`} />
            </button>
            {servicesOpen && (
              <div className="absolute left-0 top-full mt-2 w-64 rounded-xl border border-white/10 bg-gray-900 p-2 shadow-xl shadow-black/40">
                {MENU_SERVICES.map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    className="block rounded-lg px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
                  >
                    {l.label}
                  </a>
                ))}
              </div>
            )}
          </div>
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="px-3 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors"
            >
              {l.label}
            </a>
          ))}
          <a
            href={CONTACT.href}
            className="ml-2 px-4 py-2.5 rounded-xl bg-linear-to-r/srgb from-teal-500 to-cyan-600 text-white text-sm font-medium hover:brightness-110 transition-[filter]"
          >
            {CONTACT.label}
          </a>
        </nav>

        <button
          type="button"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
          className="lg:hidden p-2 text-gray-300 hover:text-white transition-colors"
        >
          {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {menuOpen && (
        <nav
          aria-label="Menu"
          className="lg:hidden border-t border-white/10 bg-gray-950 px-4 py-4 max-h-[calc(100dvh-5rem)] overflow-y-auto flex flex-col gap-2"
        >
          <button
            type="button"
            aria-expanded={servicesOpen}
            onClick={(e) => {
              e.stopPropagation(); // the click-away listener must not close what this just opened
              setServicesOpen((open) => !open);
            }}
            className={`${menuLink} flex items-center justify-center gap-1.5 w-full`}
          >
            Services
            <ChevronDown className={`w-4 h-4 transition-transform duration-150 ${servicesOpen ? "rotate-180" : ""}`} />
          </button>
          {servicesOpen && (
            <div className="grid grid-cols-2 gap-2">
              {MENU_SERVICES.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  className="flex items-center justify-center rounded-xl bg-gray-900 px-2 py-3 text-center text-sm text-gray-300 hover:text-white transition-colors"
                >
                  {l.label}
                </a>
              ))}
            </div>
          )}
          {NAV_LINKS.map((l) => (
            <a key={l.href} href={l.href} className={menuLink}>
              {l.label}
            </a>
          ))}
          <a
            href={CONTACT.href}
            className="block px-4 py-3 rounded-xl bg-linear-to-r/srgb from-teal-500 to-cyan-600 text-white font-medium text-center"
          >
            {CONTACT.label}
          </a>
        </nav>
      )}
    </header>
  );
};

export default Navigation;
